'use client';

import { useSearchParams } from 'next/navigation';
import {
  SessionProvider as NextAuthSessionProvider,
  useSession,
} from 'next-auth/react';
import type { ReactNode } from 'react';
import { Suspense, useEffect } from 'react';

interface SessionProviderProps {
  children: ReactNode;
}

/**
 * Detects ?session=refresh in the URL and triggers a session refetch.
 * Used after server-side redirects (e.g., password reset) where the
 * next-auth client cache is stale.
 *
 * Uses useSearchParams() so that soft navigations (e.g. Server Action
 * redirect) trigger a re-render and the effect re-evaluates.
 */
function SessionRefresh() {
  const { update } = useSession();
  const searchParams = useSearchParams();
  const needsRefresh = searchParams.get('session') === 'refresh';

  useEffect(() => {
    if (!needsRefresh) return;

    // Remove param from URL immediately to prevent re-triggers
    const params = new URLSearchParams(searchParams.toString());
    params.delete('session');
    const query = params.toString();
    const cleanUrl = query
      ? `${window.location.pathname}?${query}`
      : window.location.pathname;
    window.history.replaceState(null, '', cleanUrl);

    // Then refresh the session
    update();
  }, [needsRefresh, update, searchParams]);

  return null;
}

export function SessionProvider({ children }: SessionProviderProps) {
  return (
    <NextAuthSessionProvider>
      <Suspense fallback={null}>
        <SessionRefresh />
      </Suspense>
      {children}
    </NextAuthSessionProvider>
  );
}
